import { Customer, Address, ApiResponse } from '../types';
import { get, post, put, del, patch } from './api-client';

const BASE_URL = '/customers';

export interface CustomerStats {
  total_customers: number;
  current_month_customers: number;
  previous_month_customers: number;
  month_percentage: number;
}

export const getCustomers = async (): Promise<ApiResponse<Customer[]>> => {
  const data = await get<Customer[]>(BASE_URL + '/');
  return { data, success: true };
};

export const getCustomerStats = async (): Promise<ApiResponse<CustomerStats>> => {
  const data = await get<CustomerStats>(BASE_URL + '/stats');
  return { data, success: true };
};

export const searchCustomers = async (searchTerm: string): Promise<ApiResponse<Customer[]>> => {
  const data = await get<Customer[]>(`${BASE_URL}/?search=${encodeURIComponent(searchTerm)}`);
  return { data, success: true };
};

export const getCustomerById = async (customerId: string): Promise<ApiResponse<Customer>> => {
  const data = await get<Customer>(`${BASE_URL}/${customerId}`);
  return { data, success: true };
};

/**
 * Fetches a customer with their addresses
 */
export const getCustomerWithAddresses = async (
  customerId: string
): Promise<ApiResponse<{ addresses: Address[]; contacts: Contact[] }>> => {
  const data = await get<{ addresses: Address[]; contacts: Contact[] }>(
    `${BASE_URL}/${customerId}/addresses-and-contacts`
  );
  return { data, success: true };
};

// For backward compatibility, create a new function that includes contacts
/**
 * Fetches a customer with their addresses and contacts
 */
export const getCustomerWithAddressesAndContacts = async (
  customerId: string
): Promise<ApiResponse<{ addresses: Address[]; contacts: Contact[] }>> => {
  const data = await get<{ addresses: Address[]; contacts: Contact[] }>(
    `${BASE_URL}/${customerId}/addresses-and-contacts`
  );
  return { data, success: true };
};

export const createCustomer = async (
  customer: Omit<Customer, 'id' | 'created_at'>
): Promise<ApiResponse<Customer>> => {
  const data = await post<Customer, Omit<Customer, 'id' | 'created_at'>>(BASE_URL + '/', customer);
  return { data, success: true };
};

export const updateCustomer = async (
  customerId: string,
  customer: Partial<Customer>
): Promise<ApiResponse<Customer>> => {
  const data = await put<Customer, Partial<Customer>>(`${BASE_URL}/${customerId}`, customer);
  return { data, success: true };
};

export const patchCustomer = async (
  customerId: string,
  customer: Partial<Customer>
): Promise<ApiResponse<Customer>> => {
  const data = await patch<Customer, Partial<Customer>>(`${BASE_URL}/${customerId}`, customer);
  return { data, success: true };
};

export const deleteCustomer = async (customerId: string): Promise<ApiResponse<void>> => {
  await del<void>(`${BASE_URL}/${customerId}`);
  return { data: undefined, success: true };
};

// Address endpoints
export const getAddresses = async (customerId: string): Promise<ApiResponse<Address[]>> => {
  const data = await get<Address[]>(`${BASE_URL}/${customerId}/addresses`);
  return { data, success: true };
};

export const createAddress = async (
  customerId: string,
  address: Omit<Address, 'id' | 'customer_id'>
): Promise<ApiResponse<Address>> => {
  const data = await post<Address, Omit<Address, 'id' | 'customer_id'>>(
    `${BASE_URL}/${customerId}/addresses`,
    address
  );
  return { data, success: true };
};

export const updateAddress = async (
  customerId: string,
  addressId: string,
  address: Partial<Address>
): Promise<ApiResponse<Address>> => {
  const data = await put<Address, Partial<Address>>(
    `${BASE_URL}/${customerId}/addresses/${addressId}`,
    address
  );
  return { data, success: true };
};

export const patchAddress = async (
  customerId: string,
  addressId: string,
  address: Partial<Address>
): Promise<ApiResponse<Address>> => {
  const data = await patch<Address, Partial<Address>>(
    `${BASE_URL}/${customerId}/addresses/${addressId}`,
    address
  );
  return { data, success: true };
};

export const deleteAddress = async (
  customerId: string,
  addressId: string
): Promise<ApiResponse<void>> => {
  await del<void>(`${BASE_URL}/${customerId}/addresses/${addressId}`);
  return { data: undefined, success: true };
};

export const setDefaultAddress = async (
  customerId: string,
  addressId: string
): Promise<ApiResponse<void>> => {
  await put<void, Record<string, never>>(
    `${BASE_URL}/${customerId}/addresses/${addressId}/default`,
    {}
  );
  return { data: undefined, success: true };
};

// Contact endpoints
export interface Contact {
  id: string;
  customer_id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  is_primary: boolean;
}

export const createContact = async (
  customerId: string,
  contact: Omit<Contact, 'id' | 'customer_id'>
): Promise<ApiResponse<Contact>> => {
  const data = await post<Contact, Omit<Contact, 'id' | 'customer_id'>>(
    `${BASE_URL}/${customerId}/contacts`,
    contact
  );
  return { data, success: true };
};

export const updateContact = async (
  customerId: string,
  contactId: string,
  contact: Partial<Contact>
): Promise<ApiResponse<Contact>> => {
  const data = await put<Contact, Partial<Contact>>(
    `${BASE_URL}/${customerId}/contacts/${contactId}`,
    contact
  );
  return { data, success: true };
};

export const patchContact = async (
  customerId: string,
  contactId: string,
  contact: Partial<Contact>
): Promise<ApiResponse<Contact>> => {
  const data = await patch<Contact, Partial<Contact>>(
    `${BASE_URL}/${customerId}/contacts/${contactId}`,
    contact
  );
  return { data, success: true };
};

export const deleteContact = async (
  customerId: string,
  contactId: string
): Promise<ApiResponse<void>> => {
  await del<void>(`${BASE_URL}/${customerId}/contacts/${contactId}`);
  return { data: undefined, success: true };
};

export const getContacts = async (customerId: string): Promise<ApiResponse<Contact[]>> => {
  const data = await get<Contact[]>(`${BASE_URL}/${customerId}/contacts`);
  return { data, success: true };
};
